import { AbstractBuildstep } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import type * as THREE from "three";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { LTPartType } from "../enum/PartType";
import { RectType } from "../enum/RectType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PanelColor } from "../interfaces/PanelColor";
import type { PanelDefinition } from "../interfaces/PanelProperties";
import { RectSizeHelper } from "../structure/RectSizeHelper";

/**
 * Dependency data required by createPart for an Outer Panel.
 * Calculated by selectDependencies.
 */
interface OuterPanelDependencyData {
  rectId: string;
  side: Sides;
  size: THREE.Vector2; // The calculated final size
  position: THREE.Vector3; // The calculated final position
  strength: number;
  orientation?: number; // Optional orientation from blueprint/settings - currently unused
  overlaps: number;
  group: string | undefined; // Group from the source rect
  color: PanelColor; // Updated type from string to PanelColor
}

export class OuterPanelBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  PanelDefinition,
  OuterPanelDependencyData // Specify the dependency data type
> {
  constructor() {
    super("OuterPanelBuildStep");
  }

  // Helper: apply edge-driven size adjustments; returns overlaps added
  private applyEdgeAdjustment(
    rect: FlightCaseBlueprint["rects"][number],
    edgeIndex: number,
    settings: FlightCaseSettings,
    panelSize: RectSizeHelper,
  ): number {
    const edge = rect.edges[edgeIndex];
    if (!edge) return 0;
    const neighbour = rect.getNeighbour(edgeIndex);
    const strength = settings.exteriorOptions.strength;

    if (edge.type === EdgeType.FROMCUT) {
      panelSize.shrink(edgeIndex, settings.exteriorOptions.profiles.extra / 2);
      return 0;
    }

    if (
      neighbour?.type === RectType.COVERSIDE &&
      rect.type === RectType.COVERPLATE
    ) {
      panelSize.expand(edgeIndex, strength);
      return 1;
    }

    if (
      neighbour?.type === RectType.COVERSIDE &&
      rect.type !== RectType.COVERPLATE &&
      rect.side !== Sides.LEFT &&
      rect.side !== Sides.RIGHT
    ) {
      panelSize.expand(edgeIndex, strength);
      return 1;
    }

    if (
      rect.type === RectType.COVERPLATE &&
      neighbour?.type === RectType.COVERPLATE &&
      neighbour.side > rect.side
    ) {
      panelSize.expand(edgeIndex, strength);
      return 1;
    }
    return 0;
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, OuterPanelDependencyData> {
    const dependencies = new Map<string, OuterPanelDependencyData>();

    for (const rect of blueprint.rects) {
      const panelSize = new RectSizeHelper(rect);
      let overlaps = 0;

      for (const edgeIndex of [
        EdgeIndex.RIGHT,
        EdgeIndex.TOP,
        EdgeIndex.LEFT,
        EdgeIndex.BOTTOM,
      ]) {
        overlaps += this.applyEdgeAdjustment(
          rect,
          edgeIndex,
          settings,
          panelSize,
        );
      }

      const partId = `outerpanel_${rect.id}`;
      dependencies.set(partId, {
        rectId: rect.id,
        side: rect.side,
        size: panelSize.size.clone(), // Clone to prevent mutation issues
        position: panelSize.pos3d.clone(), // Clone to prevent mutation issues
        strength: rect.strength,
        group: rect.group,
        color: settings.exteriorOptions.color,
        orientation: undefined, // Set orientation explicitly to undefined for now
        overlaps,
      });
    }
    return dependencies;
  }

  public createPart(
    partId: string, // partId is available but not explicitly used in the return object structure
    dependencies: OuterPanelDependencyData,
  ): PanelDefinition {
    // Resolve orientation (defaulting if undefined)
    const orientation = dependencies.orientation ?? 0;
    // Calculate final rotation based *only* on dependencies
    const rotation = this.rotateOntoSide(dependencies.side, orientation);

    // Construct the final PanelDefinition using only dependencies and constants
    return {
      id: partId,
      // Values directly from dependencies
      rectId: dependencies.rectId,
      side: dependencies.side,
      size: dependencies.size, // Use the provided Vector2
      position: dependencies.position, // Use the provided Vector3
      strength: dependencies.strength,
      orientation: orientation, // Use the resolved orientation
      group: dependencies.group ?? "", // Provide default empty string for undefined group
      color: dependencies.color, // Assign PanelColor type

      // Calculated value
      rotation: rotation,

      // Constant values for this part type
      type: LTPartType.Panel,
      buildstepName: this.buildstepName, // Build step adds its own name

      // Values handled later or defaults
      overlaps: dependencies.overlaps,
      cutouts: [], // Initialized as empty, handled by interaction steps
    };
  }
}
